import { useContext, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import Cookies from 'js-cookie';

import { CreateCustomerLeadApiResponse } from 'app/shared/graphql/customerLeads/mutationHooks';
import { CREATE_CUSTOMER_LEAD } from 'app/shared/graphql/customerLeads/mutations';
import { AuthContext } from 'app/shared/context/Auth';
import { useCityContext } from 'app/shared/context/City';

import { useAnalyticsContext } from './analytics';

const KLAVIYO_EMAIL_AND_SMS_FORM_IDS: {
  [key: string]: string | undefined;
} = {
  US: process.env.KLAVIYO_EMAIL_AND_SMS_FORM_ID_US,
  CA: process.env.KLAVIYO_EMAIL_AND_SMS_FORM_ID_CA,
  GB: process.env.KLAVIYO_EMAIL_AND_SMS_FORM_ID_GB,
};

export const useKlaviyoEmailAndSmsPopup = () => {
  const { closestCities } = useCityContext();
  const { trackAnalyticsEvent } = useAnalyticsContext();
  const { loggedIn } = useContext(AuthContext);
  const closestCity = closestCities?.[0];
  const countryCode = closestCity?.country?.countryCode;
  const [createCustomerLead] =
    useMutation<CreateCustomerLeadApiResponse>(CREATE_CUSTOMER_LEAD);
  const [formDisplayed, setFormDisplayed] = useState(false);

  const isOAndOCountry =
    countryCode === 'US' || countryCode === 'CA' || countryCode === 'GB';

  const isOAndOCity = closestCity?.cityOperatingModel
    ? ['sofar_operated', 'sofar_operated_and_curator', 'producer'].includes(
        closestCity.cityOperatingModel
      )
    : false;

  const klaviyoEmailAndSMSPopupDisplayed = Cookies.get(
    'klaviyoEmailAndSMSPopupDisplayed'
  );
  const shouldDisplayKlaviyoForm =
    !loggedIn &&
    isOAndOCountry &&
    isOAndOCity &&
    !klaviyoEmailAndSMSPopupDisplayed;

  const formId = countryCode && KLAVIYO_EMAIL_AND_SMS_FORM_IDS[countryCode];

  useEffect(() => {
    if (formDisplayed || !shouldDisplayKlaviyoForm || !formId) {
      return;
    }

    const eventHandler = (e: any) => {
      const eventType = e.detail.type;
      if (eventType == 'open') {
        trackAnalyticsEvent('Klaviyo Email And SMS Form displayed', {
          form_id: formId,
          closest_city_slug: closestCity?.cachedSlug,
        });

        const twoWeeksFromNow = new Date(Date.now() + 60 * 60 * 1000 * 24 * 14);
        Cookies.set('klaviyoEmailAndSMSPopupDisplayed', 'true', {
          expires: twoWeeksFromNow,
        });
      }

      if (eventType == 'stepSubmit') {
        const leadEmailAddress = e.detail.metaData?.$email;
        const leadPhoneNumber = e.detail.metaData?.$phone_number;

        trackAnalyticsEvent('Klaviyo Email And SMS Form step submitted', {
          lead_email_address: leadEmailAddress,
          lead_phone_number: leadPhoneNumber,
        });

        if (leadEmailAddress && !leadPhoneNumber && closestCity) {
          createCustomerLead({
            variables: {
              email: leadEmailAddress,
              cityId: closestCity.id,
              sourceUrl: window.location.href,
            },
          }).catch(() =>
            trackAnalyticsEvent(
              'Klaviyo Email And SMS Form customer lead failure',
              {
                lead_email_address: leadEmailAddress,
                lead_phone_number: leadPhoneNumber,
              }
            )
          );

          window.klaviyo.identify({
            city_slug: closestCity.cachedSlug,
            home_city_title: closestCity.title,
            home_city_link_url: `https://www.sofarsounds.com/cities/${closestCity.cachedSlug}`,
          });
        }
      }
    };

    setFormDisplayed(true);
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['openForm', formId]);
    window.addEventListener('klaviyoForms', eventHandler);

    return () => window.removeEventListener('klaviyoForms', eventHandler);
  }, [shouldDisplayKlaviyoForm, formId, closestCity]);
};
